import React, { useState } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faUser,
  faTag,
  faArrowRight,
  faBookOpen,
  faScaleBalanced,
  faComment,
  faBookmark,
  faClock,
  faChevronRight,
  faNewspaper,
  faTimes,
  faEye,
  faShare
} from "@fortawesome/free-solid-svg-icons";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { 
      duration: 0.8, 
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: "easeOut" }
  }
};

// Blog posts array
const blogPosts = [
  {
    id: 1,
    title: "Navigating Recent Changes in Corporate Tax Regulations",
    excerpt: "Understanding the implications of the latest corporate tax amendments and compliance requirements for businesses in 2024.",
    category: "Taxation",
    author: "Adv. Rajesh Sharma",
    date: "Mar 15, 2024",
    readTime: "8 min read",
    tags: ["Corporate Tax", "Compliance", "Business", "Regulations", "2024"],
    imageColor: "gold",
    featured: true,
    fullContent: `
      <h2>Understanding Corporate Tax Amendments in 2024</h2>
      <p>The year 2024 has brought significant changes to corporate tax regulations that affect businesses of all sizes. As experts at RNA Law Chambers, we've analyzed these amendments to help you understand their implications.</p>
      
      <h3>Key Changes Introduced</h3>
      <ul>
        <li><strong>Reduced Corporate Tax Rates:</strong> The corporate tax rate has been reduced from 30% to 25% for companies with turnover up to ₹400 crore.</li>
        <li><strong>New Compliance Requirements:</strong> Mandatory digital filing for all corporate returns, with stricter penalties for non-compliance.</li>
        <li><strong>International Tax Provisions:</strong> Implementation of BEPS 2.0 Pillar Two rules affecting multinational corporations.</li>
        <li><strong>Tax Incentives:</strong> Enhanced deductions for R&D investments and green energy initiatives.</li>
      </ul>
      
      <h3>Compliance Requirements</h3>
      <p>Businesses need to adapt their accounting systems and compliance procedures to meet the new requirements:</p>
      <ol>
        <li>Update financial reporting systems to capture new data points</li>
        <li>Implement digital tax filing processes</li>
        <li>Review international transactions for BEPS compliance</li>
        <li>Document tax positions and maintain proper records</li>
      </ol>
      
      <h3>Impact on Different Business Sectors</h3>
      <p>The amendments have varying impacts across sectors:</p>
      
      <h4>Manufacturing Sector</h4>
      <p>Manufacturing companies benefit from increased deductions for capital expenditure and R&D activities. However, they must comply with stricter transfer pricing documentation.</p>
      
      <h4>Service Sector</h4>
      <p>Service companies face challenges with the digital service tax provisions and must ensure proper classification of revenue streams.</p>
      
      <h4>Startups and MSMEs</h4>
      <p>Smaller businesses enjoy reduced tax rates but must maintain meticulous records to avail of these benefits.</p>
      
      <h3>Recommended Actions</h3>
      <p>Based on our analysis, we recommend:</p>
      <ul>
        <li>Conduct a comprehensive tax health check</li>
        <li>Update internal compliance procedures</li>
        <li>Review and document tax positions</li>
        <li>Consider restructuring if beneficial under new rules</li>
        <li>Implement regular tax training for finance teams</li>
      </ul>
      
      <h3>Conclusion</h3>
      <p>The 2024 tax amendments present both challenges and opportunities. While compliance requirements have increased, the reduced rates and new incentives can significantly benefit compliant businesses. Professional tax advisory is more crucial than ever to navigate these changes effectively.</p>
      
      <p><strong>Disclaimer:</strong> This article provides general information and should not be considered legal advice. Please consult with our tax experts for advice specific to your situation.</p>
    `,
    views: 1245,
    comments: 28
  },
  {
    id: 2,
    title: "Digital Transformation in Legal Practice: Challenges and Opportunities",
    excerpt: "How technology is reshaping legal services and what law firms need to adapt to stay competitive in the digital age.",
    category: "Legal Tech",
    author: "Adv. Priya Mehta",
    date: "Mar 10, 2024",
    readTime: "10 min read",
    tags: ["Legal Tech", "Digital Transformation", "AI", "Innovation", "Future"],
    imageColor: "primary-blue",
    featured: true,
    fullContent: `
      <h2>The Digital Revolution in Legal Services</h2>
      <p>The legal industry is undergoing a digital transformation that is reshaping how legal services are delivered and consumed. At RNA Law Chambers, we've embraced this change while understanding the challenges it presents.</p>
      
      <h3>Key Technologies Impacting Legal Practice</h3>
      <ul>
        <li><strong>Artificial Intelligence:</strong> AI-powered contract review, legal research, and prediction tools</li>
        <li><strong>Blockchain:</strong> Smart contracts and secure document management</li>
        <li><strong>Cloud Computing:</strong> Remote access to case files and collaborative tools</li>
        <li><strong>Legal Tech Platforms:</strong> Case management, billing, and client portals</li>
        <li><strong>Data Analytics:</strong> Predictive analysis for case outcomes and legal strategy</li>
      </ul>
      
      <h3>Challenges in Digital Transformation</h3>
      <p>Adopting digital technologies presents several challenges:</p>
      
      <h4>1. Data Security and Privacy</h4>
      <p>Legal firms handle sensitive client information that requires robust security measures. Compliance with data protection regulations like GDPR is crucial.</p>
      
      <h4>2. Ethical Considerations</h4>
      <p>The use of AI raises questions about attorney-client privilege, bias in algorithms, and professional responsibility.</p>
      
      <h4>3. Training and Adaptation</h4>
      <p>Legal professionals need training to effectively use new technologies while maintaining traditional legal skills.</p>
      
      <h4>4. Cost of Implementation</h4>
      <p>Initial investment in technology infrastructure can be substantial, especially for smaller firms.</p>
      
      <h3>Opportunities Created by Digital Transformation</h3>
      
      <h4>Enhanced Efficiency</h4>
      <p>Automation of routine tasks frees up lawyers to focus on complex legal work and client relationships.</p>
      
      <h4>Improved Client Service</h4>
      <p>Real-time case updates, secure document sharing, and 24/7 accessibility through client portals.</p>
      
      <h4>Data-Driven Decisions</h4>
      <p>Analytics provide insights for case strategy, resource allocation, and business development.</p>
      
      <h4>Global Reach</h4>
      <p>Digital platforms enable serving clients across geographical boundaries without physical presence.</p>
      
      <h3>Our Digital Transformation Journey at RNA</h3>
      <p>At RNA Law Chambers, we've implemented:</p>
      <ol>
        <li>Secure cloud-based case management system</li>
        <li>AI-powered legal research tools</li>
        <li>Client portal for real-time updates</li>
        <li>Digital signature and document management</li>
        <li>Regular cybersecurity audits</li>
      </ol>
      
      <h3>Future Trends to Watch</h3>
      <ul>
        <li>Virtual court proceedings becoming standard</li>
        <li>AI-assisted legal drafting and analysis</li>
        <li>Blockchain for legal document verification</li>
        <li>Predictive analytics for litigation outcomes</li>
        <li>Integration of legal tech with business systems</li>
      </ul>
      
      <h3>Recommendations for Law Firms</h3>
      <p>Based on our experience, we recommend:</p>
      <ol>
        <li>Start with a digital transformation strategy</li>
        <li>Prioritize data security and compliance</li>
        <li>Invest in training and change management</li>
        <li>Begin with pilot projects before full implementation</li>
        <li>Regularly review and update technology stack</li>
      </ol>
      
      <p>The digital transformation of legal practice is inevitable. Embracing it strategically can lead to improved efficiency, better client service, and competitive advantage.</p>
    `,
    views: 1890,
    comments: 42
  },
  {
    id: 3,
    title: "Supreme Court Ruling: Impact on Contract Enforcement",
    excerpt: "Analysis of the recent Supreme Court judgment and its implications for contract law and commercial disputes.",
    category: "Litigation",
    author: "Adv. Amit Patel",
    date: "Mar 18, 2024",
    readTime: "6 min read",
    tags: ["Contract Law", "Supreme Court", "Commercial Disputes", "Force Majeure", "Litigation"],
    imageColor: "gold",
    featured: false,
    fullContent: `
      <h2>Landmark Supreme Court Decision on Contract Law</h2>
      <p>The recent Supreme Court ruling has significantly altered the landscape of contract enforcement in India. This analysis examines the key aspects of the judgment and its practical implications.</p>
      
      <h3>Case Background</h3>
      <p>The case involved a complex commercial dispute between two multinational corporations regarding breach of contract. The key issue was the interpretation of force majeure clauses in light of unprecedented global events.</p>
      
      <h3>Key Legal Principles Established</h3>
      <ul>
        <li><strong>Strict Interpretation of Force Majeure:</strong> The Court emphasized that force majeure clauses must be interpreted strictly and cannot be invoked for foreseeable events.</li>
        <li><strong>Good Faith Obligation:</strong> Parties must demonstrate genuine efforts to mitigate damages before claiming breach.</li>
        <li><strong>Digital Evidence Admissibility:</strong> Emails, electronic communications, and digital signatures are given equal weight as traditional evidence.</li>
        <li><strong>Expedited Dispute Resolution:</strong> Encouragement for arbitration and mediation before litigation.</li>
      </ul>
      
      <h3>Practical Implications for Businesses</h3>
      
      <h4>Contract Drafting</h4>
      <p>Businesses need to review and update their standard contract templates to incorporate:</p>
      <ul>
        <li>Clearer force majeure definitions</li>
        <li>Digital execution provisions</li>
        <li>Dispute resolution mechanisms</li>
        <li>Mitigation obligation clauses</li>
      </ul>
      
      <h4>Risk Management</h4>
      <p>Companies should implement:</p>
      <ol>
        <li>Regular contract audits</li>
        <li>Training for contract managers</li>
        <li>Documentation of all communications</li>
        <li>Alternative dispute resolution strategies</li>
      </ol>
      
      <h4>Compliance Requirements</h4>
      <p>The judgment imposes new compliance obligations:</p>
      <ul>
        <li>Maintain digital records for 10 years</li>
        <li>Implement electronic signature systems</li>
        <li>Establish contract management protocols</li>
        <li>Regular legal compliance reviews</li>
      </ul>
      
      <h3>Industry-Specific Impacts</h3>
      
      <h4>Real Estate and Construction</h4>
      <p>Project delays due to external factors must now be documented extensively to invoke force majeure protection.</p>
      
      <h4>Manufacturing and Supply Chain</h4>
      <p>Supply chain disruptions require immediate communication and mitigation efforts to preserve contractual rights.</p>
      
      <h4>Technology and Services</h4>
      <p>Service level agreements must include specific provisions for remote work and digital delivery.</p>
      
      <h3>Recommendations</h3>
      <p>Based on this ruling, we recommend:</p>
      <ol>
        <li>Immediate review of all active contracts</li>
        <li>Update standard operating procedures</li>
        <li>Train employees on new requirements</li>
        <li>Consult legal counsel for high-value agreements</li>
        <li>Implement contract management software</li>
      </ol>
      
      <h3>Conclusion</h3>
      <p>This Supreme Court judgment represents a significant shift in contract law jurisprudence. Businesses that proactively adapt to these changes will be better positioned to protect their interests and avoid costly litigation.</p>
    `,
    views: 1560,
    comments: 35
  },
  {
    id: 4,
    title: "GST Updates: What Businesses Need to Know",
    excerpt: "Latest GST amendments and compliance requirements for financial year 2023-24.",
    category: "Taxation",
    author: "Adv. Neha Gupta",
    date: "Mar 12, 2024",
    readTime: "7 min read",
    tags: ["GST", "Compliance", "Business", "Tax Updates"],
    imageColor: "primary-blue",
    featured: false,
    fullContent: `
      <h2>Comprehensive Guide to GST Updates 2024</h2>
      <p>The Goods and Services Tax (GST) framework has undergone several important updates for the financial year 2023-24. This article outlines the key changes and their implications for businesses.</p>
      
      <h3>Major GST Amendments</h3>
      <ul>
        <li><strong>Increased Threshold Limits:</strong> The threshold for GST registration has been increased to ₹40 lakh for goods and ₹20 lakh for services.</li>
        <li><strong>New E-invoicing Requirements:</strong> Mandatory e-invoicing for businesses with turnover above ₹5 crore.</li>
        <li><strong>Simplified Returns:</strong> Introduction of simplified GST returns for small taxpayers.</li>
        <li><strong>Enhanced Input Tax Credit:</strong> Stricter conditions for claiming input tax credit to prevent fraud.</li>
      </ul>
      
      <h3>Compliance Requirements</h3>
      <ol>
        <li>Monthly/Quarterly GST returns filing</li>
        <li>E-way bill generation for goods movement</li>
        <li>Maintenance of digital records</li>
        <li>Regular GST audits for large businesses</li>
      </ol>
      
      <h3>Sector-Specific Implications</h3>
      <p>Different industries are affected differently by these changes. Consult with our tax experts for sector-specific guidance.</p>
    `,
    views: 980,
    comments: 19
  },
  {
    id: 5,
    title: "Protecting Intellectual Property in the Digital Age",
    excerpt: "Strategies for safeguarding intellectual property rights in an increasingly digital business environment.",
    category: "Intellectual Property",
    author: "Adv. Vikram Singh",
    date: "Mar 8, 2024",
    readTime: "9 min read",
    tags: ["IP", "Digital Rights", "Copyright", "Trademark"],
    imageColor: "gold",
    featured: false,
    fullContent: `
      <h2>Intellectual Property Protection Strategies</h2>
      <p>In today's digital landscape, protecting intellectual property has become more challenging and more important than ever. Here are key strategies for safeguarding your IP assets.</p>
      
      <h3>Key Protection Mechanisms</h3>
      <ul>
        <li><strong>Trademark Registration:</strong> Protect your brand names, logos, and slogans</li>
        <li><strong>Copyright Protection:</strong> Safeguard creative works, software, and digital content</li>
        <li><strong>Patent Filing:</strong> Protect inventions and technological innovations</li>
        <li><strong>Trade Secret Management:</strong> Protect confidential business information</li>
      </ul>
      
      <h3>Digital IP Challenges</h3>
      <p>The digital environment presents unique challenges including online piracy, domain name squatting, and cross-border infringement.</p>
      
      <h3>Best Practices</h3>
      <ol>
        <li>Conduct regular IP audits</li>
        <li>Implement digital rights management</li>
        <li>Monitor online platforms for infringement</li>
        <li>Develop comprehensive IP policies</li>
      </ol>
    `,
    views: 1120,
    comments: 27
  },
  {
    id: 6,
    title: "Cross-Border Transactions: Legal Considerations",
    excerpt: "Key legal aspects to consider when engaging in international business transactions and trade agreements.",
    category: "International Law",
    author: "Adv. Sonia Kapoor",
    date: "Mar 5, 2024",
    readTime: "11 min read",
    tags: ["International", "Trade", "Compliance", "Global Business"],
    imageColor: "primary-blue",
    featured: false,
    fullContent: `
      <h2>International Business Transactions Guide</h2>
      <p>Cross-border transactions involve complex legal considerations. Understanding these is crucial for successful international business operations.</p>
      
      <h3>Key Legal Considerations</h3>
      <ul>
        <li><strong>Choice of Law and Jurisdiction:</strong> Determining which country's laws will govern the transaction</li>
        <li><strong>Tax Implications:</strong> Understanding international tax treaties and obligations</li>
        <li><strong>Regulatory Compliance:</strong> Adhering to international trade regulations and sanctions</li>
        <li><strong>Dispute Resolution:</strong> Mechanisms for resolving cross-border disputes</li>
      </ul>
      
      <h3>Risk Management Strategies</h3>
      <p>Effective risk management is essential for successful international transactions. Our experts can help you navigate these complexities.</p>
    `,
    views: 890,
    comments: 15
  },
  {
    id: 7,
    title: "Startup Legal Framework: Essential Requirements",
    excerpt: "Complete guide to legal requirements and compliance for new startups and entrepreneurs.",
    category: "Corporate Law",
    author: "Adv. Rohit Malhotra",
    date: "Feb 28, 2024",
    readTime: "8 min read",
    tags: ["Startup", "Entrepreneurship", "Compliance", "Business Formation"],
    imageColor: "gold",
    featured: false,
    fullContent: `
      <h2>Startup Legal Essentials</h2>
      <p>Launching a startup involves navigating a complex legal landscape. This guide covers the essential legal requirements for new businesses.</p>
      
      <h3>Key Legal Steps</h3>
      <ul>
        <li><strong>Business Structure Selection:</strong> Choosing between sole proprietorship, partnership, LLP, or company</li>
        <li><strong>Registration and Licensing:</strong> Obtaining necessary registrations and licenses</li>
        <li><strong>Intellectual Property Protection:</strong> Securing trademarks, patents, and copyrights</li>
        <li><strong>Employment Agreements:</strong> Drafting proper employment contracts</li>
        <li><strong>Shareholder Agreements:</strong> Defining rights and responsibilities of stakeholders</li>
      </ul>
      
      <h3>Compliance Requirements</h3>
      <p>Startups must comply with various legal requirements including tax filings, statutory audits, and regulatory compliance.</p>
    `,
    views: 1450,
    comments: 32
  },
  {
    id: 8,
    title: "Merger & Acquisition: Legal Due Diligence",
    excerpt: "Comprehensive guide to legal due diligence process in mergers and acquisitions.",
    category: "Corporate Law",
    author: "Adv. Sanjay Rao",
    date: "Feb 15, 2024",
    readTime: "12 min read",
    tags: ["M&A", "Due Diligence", "Corporate", "Acquisitions"],
    imageColor: "primary-blue",
    featured: false,
    fullContent: `
      <h2>M&A Legal Due Diligence Process</h2>
      <p>Thorough legal due diligence is critical for successful mergers and acquisitions. This guide outlines the key aspects of the due diligence process.</p>
      
      <h3>Due Diligence Checklist</h3>
      <ul>
        <li><strong>Corporate Records:</strong> Review of company formation documents and governance</li>
        <li><strong>Contracts Review:</strong> Analysis of all material contracts and agreements</li>
        <li><strong>Intellectual Property Audit:</strong> Assessment of IP assets and protection</li>
        <li><strong>Litigation History:</strong> Review of past and pending legal proceedings</li>
        <li><strong>Regulatory Compliance:</strong> Verification of compliance with applicable laws</li>
        <li><strong>Employee Matters:</strong> Review of employment agreements and benefits</li>
      </ul>
      
      <h3>Risk Assessment</h3>
      <p>Identifying and assessing legal risks is crucial for successful M&A transactions. Our experienced M&A lawyers can guide you through this complex process.</p>
    `,
    views: 1200,
    comments: 24
  }
];

const popularTags = [
  "Taxation", "Legal Tech", "Litigation", "Intellectual Property", 
  "International Law", "Corporate Law", "GST", "Compliance", "Startup", "M&A"
];

const recentComments = [
  {
    author: "Rahul Kumar",
    comment: "Excellent analysis! The GST compliance section was particularly helpful.",
    post: "GST Updates: What Businesses Need to Know",
    time: "2 days ago"
  },
  {
    author: "Meera Shah",
    comment: "Very insightful article about IP protection in digital spaces.",
    post: "Protecting Intellectual Property in the Digital Age",
    time: "4 days ago"
  },
  {
    author: "Arun Joshi",
    comment: "Clear and practical advice for startups. Thank you!",
    post: "Startup Legal Framework: Essential Requirements",
    time: "1 week ago"
  }
];

export const BlogPage = () => {
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [isArticleOpen, setIsArticleOpen] = useState(false);

  const handleReadMore = (post) => {
    setSelectedArticle(post);
    setIsArticleOpen(true);
    // Scroll to top when opening article
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCloseArticle = () => {
    setIsArticleOpen(false);
    setTimeout(() => setSelectedArticle(null), 300);
  };

  // Get featured posts
  const featuredPosts = blogPosts.filter(post => post.featured);
  // Get regular posts (non-featured)
  const regularPosts = blogPosts.filter(post => !post.featured);

  return (
    <div style={{ 
      backgroundColor: "var(--cream)",
      minHeight: "100vh",
      paddingTop: "120px",
      position: "relative"
    }}>
      {/* Background decorative elements */}
      <div className="position-fixed top-0 start-0 w-100 h-100" style={{ zIndex: 0, pointerEvents: "none", overflow: "hidden" }}>
        <div className="position-absolute top-0 end-0 w-25 h-25"
          style={{
            background: "radial-gradient(circle, var(--gold) 0%, transparent 70%)",
            opacity: 0.03,
            transform: "translate(30%, -30%)"
          }}
        ></div>
        <div className="position-absolute bottom-0 start-0 w-30 h-30"
          style={{
            background: "radial-gradient(circle, var(--primary-blue) 0%, transparent 70%)",
            opacity: 0.03,
            transform: "translate(-30%, 30%)"
          }}
        ></div>
      </div>

      <div className="container position-relative" style={{ zIndex: 1 }}>
        {/* Full Article Modal/Overlay */}
        {isArticleOpen && selectedArticle && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="position-fixed top-0 start-0 w-100 h-100 z-3"
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(4px)",
              overflowY: "auto",
              zIndex: 1000
            }}
          >
            <div className="container py-5">
              <motion.div
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="bg-white rounded-4 p-4 p-md-5 position-relative"
                style={{ minHeight: "80vh" }}
              >
                {/* Close Button */}
                <button
                  onClick={handleCloseArticle}
                  className="btn position-absolute top-0 end-0 m-4 p-2 rounded-circle"
                  style={{
                    backgroundColor: "var(--gold)",
                    color: "var(--primary-dark)",
                    zIndex: 4,
                    width: "40px",
                    height: "40px"
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>

                {/* Article Header */}
                <div className="mb-5">
                  <span className="badge px-3 py-2 mb-3"
                    style={{
                      backgroundColor: `rgba(var(--${selectedArticle.imageColor}-rgb), 0.1)`,
                      color: `var(--${selectedArticle.imageColor})`,
                      fontWeight: "500",
                      fontSize: "0.9rem"
                    }}
                  >
                    {selectedArticle.category}
                  </span>
                  <h1 className="fw-bold mb-4" style={{ color: "var(--primary-dark)" }}>
                    {selectedArticle.title}
                  </h1>
                  
                  {/* Author Info */}
                  <div className="d-flex align-items-center gap-3 mb-4">
                    <div className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: "50px",
                        height: "50px",
                        backgroundColor: `rgba(var(--${selectedArticle.imageColor}-rgb), 0.1)`,
                        color: `var(--${selectedArticle.imageColor})`
                      }}
                    >
                      <FontAwesomeIcon icon={faUser} />
                    </div>
                    <div>
                      <div className="fw-bold" style={{ color: "var(--primary-dark)" }}>
                        {selectedArticle.author}
                      </div>
                      <div className="small text-muted">
                        {selectedArticle.date} · {selectedArticle.readTime}
                      </div>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="d-flex gap-4 mb-4">
                    <div className="d-flex align-items-center gap-1">
                      <FontAwesomeIcon icon={faEye} style={{ color: "var(--text-primary)" }} />
                      <span className="small text-muted">{selectedArticle.views || 0} views</span>
                    </div>
                    <div className="d-flex align-items-center gap-1">
                      <FontAwesomeIcon icon={faComment} style={{ color: "var(--text-primary)" }} />
                      <span className="small text-muted">{selectedArticle.comments || 0} comments</span>
                    </div>
                  </div>
                </div>

                {/* Article Content */}
                <div className="article-content" 
                  style={{ 
                    fontSize: "1.1rem",
                    lineHeight: 1.8,
                    color: "var(--text-primary)"
                  }}
                  dangerouslySetInnerHTML={{ __html: selectedArticle.fullContent }}
                />

                {/* Tags */}
                <div className="mt-5 pt-4 border-top">
                  <h6 className="fw-bold mb-3" style={{ color: "var(--primary-dark)" }}>
                    <FontAwesomeIcon icon={faTag} className="me-2" style={{ color: "var(--gold)" }} />
                    Article Tags
                  </h6>
                  <div className="d-flex flex-wrap gap-2">
                    {(selectedArticle.tags || []).map((tag, idx) => (
                      <span key={idx} className="badge px-3 py-2"
                        style={{
                          backgroundColor: "rgba(var(--primary-blue-rgb), 0.1)",
                          color: "var(--primary-blue)",
                          fontSize: "0.85rem"
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Share and Actions */}
                <div className="mt-5 pt-4 border-top d-flex justify-content-between align-items-center">
                  <button className="btn px-4 py-2"
                    style={{
                      backgroundColor: "var(--gold)",
                      color: "var(--primary-dark)",
                      fontWeight: "500"
                    }}
                    onClick={handleCloseArticle}
                  >
                    Close Article
                  </button>
                  <div className="d-flex gap-2">
                    <button className="btn p-2 rounded-circle"
                      style={{
                        backgroundColor: "rgba(var(--primary-blue-rgb), 0.1)",
                        color: "var(--primary-blue)"
                      }}
                    >
                      <FontAwesomeIcon icon={faBookmark} />
                    </button>
                    <button className="btn p-2 rounded-circle"
                      style={{
                        backgroundColor: "rgba(var(--primary-blue-rgb), 0.1)",
                        color: "var(--primary-blue)"
                      }}
                    >
                      <FontAwesomeIcon icon={faShare} />
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}

        {/* Hero Section */}
        <motion.div
          className="text-center mb-5 px-3"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
        >
          <motion.div variants={fadeInUp}>
            <span className="d-inline-block px-4 py-2 rounded-pill mb-3"
              style={{
                color: "var(--gold)",
                fontWeight: "600",
                letterSpacing: "2px",
                fontSize: "0.9rem",
                textTransform: "uppercase",
                border: "1px solid var(--gold-light)",
                background: "rgba(184, 134, 11, 0.05)",
                backdropFilter: "blur(4px)"
              }}
            >
              Legal Insights & Updates
            </span>
            <motion.h4
              className="fw-bold display-6 display-md-5 mt-2"
              variants={fadeInUp}
              style={{
                fontFamily: "'Playfair Display', serif",
                fontWeight: 600,
                color: 'var(--primary-dark)',
                background: "linear-gradient(135deg, var(--primary-dark) 0%, var(--gold) 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text"
              }}
            >
              RNA Law Chambers Legal Blog
            </motion.h4>
          </motion.div>

          <motion.p
            className="fs-6 fs-md-5 mx-auto mb-3 mb-md-4"
            variants={fadeInUp}
            style={{
              maxWidth: "750px",
              color: "var(--text-primary)",
              lineHeight: 1.7
            }}
          >
            Expert legal analysis, industry insights, and practical guidance from our team of experienced attorneys.
            Stay updated with the latest legal developments and best practices.
          </motion.p>
        </motion.div>

        {/* Featured Posts */}
        <motion.div
          className="mb-5 px-3"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
        >
          <div className="row mb-4">
            <div className="col-12">
              <div className="d-flex align-items-center justify-content-between mb-4">
                <h3 className="fw-bold mb-0" style={{ color: "var(--primary-dark)" }}>
                  <FontAwesomeIcon icon={faBookOpen} className="me-3" style={{ color: "var(--gold)" }} />
                  Featured Articles
                </h3>
              </div>
            </div>
          </div>

          <div className="row g-4 mb-5">
            {featuredPosts.map((post, index) => (
              <motion.div 
                key={post.id} 
                className="col-md-6"
                variants={itemVariants}
              >
                <motion.div
                  className="rounded-4 overflow-hidden h-100 position-relative"
                  style={{
                    backgroundColor: "var(--white)",
                    border: "1px solid rgba(var(--primary-blue-rgb), 0.1)",
                    boxShadow: "var(--shadow-soft)",
                    transition: "var(--transition)"
                  }}
                  whileHover={{ 
                    y: -8,
                    boxShadow: "var(--shadow-medium)"
                  }}
                >
                  <div className="row g-0 h-100">
                    {/* Featured Image */}
                    <div className="col-md-5 position-relative">
                      <div 
                        className="h-100 d-flex align-items-center justify-content-center"
                        style={{
                          backgroundColor: `rgba(var(--${post.imageColor}-rgb), 0.1)`,
                          position: "relative",
                          overflow: "hidden"
                        }}
                      >
                        <div className="position-relative z-1 text-center p-4">
                          <div className="display-4 fw-bold mb-3"
                            style={{ 
                              color: `var(--${post.imageColor})`,
                              fontFamily: "'Playfair Display', serif"
                            }}
                          >
                            0{index + 1}
                          </div>
                          <div className="p-3 rounded-circle d-inline-block"
                            style={{
                              backgroundColor: `rgba(var(--${post.imageColor}-rgb), 0.2)`,
                              color: `var(--${post.imageColor})`
                            }}
                          >
                            <FontAwesomeIcon icon={faBookOpen} style={{ fontSize: "1.5rem" }} />
                          </div>
                        </div>
                        <div className="position-absolute top-0 start-0 w-100 h-100"
                          style={{
                            background: `radial-gradient(circle, transparent 30%, rgba(var(--${post.imageColor}-rgb), 0.05) 70%)`
                          }}
                        ></div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="col-md-7">
                      <div className="p-4 h-100 d-flex flex-column">
                        <div className="mb-3">
                          <span className="badge px-3 py-2 mb-2"
                            style={{
                              backgroundColor: `rgba(var(--${post.imageColor}-rgb), 0.1)`,
                              color: `var(--${post.imageColor})`,
                              fontWeight: "500"
                            }}
                          >
                            {post.category}
                          </span>
                          <h4 className="fw-bold mb-3" style={{ color: "var(--primary-dark)" }}>
                            {post.title}
                          </h4>
                          <p className="mb-4" style={{ color: "var(--text-primary)" }}>
                            {post.excerpt}
                          </p>
                        </div>

                        <div className="mt-auto pt-3 border-top"
                          style={{ borderColor: "rgba(var(--primary-blue-rgb), 0.1)" }}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center gap-3">
                              <div className="d-flex align-items-center gap-1">
                                <FontAwesomeIcon 
                                  icon={faUser} 
                                  style={{ 
                                    color: "var(--text-primary)",
                                    fontSize: "0.8rem"
                                  }} 
                                />
                                <small style={{ color: "var(--text-primary)" }}>
                                  {post.author}
                                </small>
                              </div>
                              <div className="d-flex align-items-center gap-1">
                                <FontAwesomeIcon 
                                  icon={faCalendarAlt} 
                                  style={{ 
                                    color: "var(--text-primary)",
                                    fontSize: "0.8rem"
                                  }} 
                                />
                                <small style={{ color: "var(--text-primary)" }}>
                                  {post.date}
                                </small>
                              </div>
                            </div>
                            <motion.button
                              className="btn btn-sm px-3"
                              style={{
                                backgroundColor: `var(--${post.imageColor})`,
                                color: post.imageColor === "gold" ? "var(--primary-dark)" : "var(--white)",
                                fontWeight: "500"
                              }}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleReadMore(post)}
                            >
                              Read Article
                              <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                            </motion.button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* All Articles Section */}
        <div className="row px-3">
          {/* Blog Posts */}
          <div className="col-lg-8 mb-5">
            {/* Section Header */}
            <motion.div
              className="mb-5"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={containerVariants}
            >
              <h3 className="fw-bold mb-4" style={{ color: "var(--primary-dark)" }}>
                <FontAwesomeIcon icon={faNewspaper} className="me-3" style={{ color: "var(--gold)" }} />
                All Legal Articles
              </h3>
              <p className="text-muted mb-4">
                Browse our complete collection of legal insights, analysis, and practical guidance.
              </p>
            </motion.div>

            {/* Blog Grid */}
            <motion.div
              className="row g-4"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={containerVariants}
            >
              {regularPosts.map((post) => (
                <motion.div 
                  key={post.id} 
                  className="col-md-6"
                  variants={itemVariants}
                >
                  <motion.div
                    className="h-100 rounded-4 overflow-hidden"
                    style={{
                      backgroundColor: "var(--white)",
                      border: "1px solid rgba(var(--primary-blue-rgb), 0.1)",
                      boxShadow: "var(--shadow-soft)",
                      transition: "var(--transition)"
                    }}
                    whileHover={{ 
                      y: -8,
                      boxShadow: "var(--shadow-medium)"
                    }}
                  >
                    {/* Post Image */}
                    <div className="position-relative" style={{ height: "200px" }}>
                      <div 
                        className="h-100 d-flex align-items-center justify-content-center"
                        style={{
                          backgroundColor: `rgba(var(--${post.imageColor}-rgb), 0.1)`,
                          position: "relative"
                        }}
                      >
                        <div className="position-relative z-1 text-center">
                          <div className="p-3 rounded-circle d-inline-block mb-3"
                            style={{
                              backgroundColor: `rgba(var(--${post.imageColor}-rgb), 0.2)`,
                              color: `var(--${post.imageColor})`
                            }}
                          >
                            <FontAwesomeIcon icon={faBookOpen} style={{ fontSize: "1.5rem" }} />
                          </div>
                          <div className="h5 fw-bold mb-0"
                            style={{ color: `var(--${post.imageColor})` }}
                          >
                            {post.category}
                          </div>
                        </div>
                      </div>
                      <div className="position-absolute top-3 end-3">
                        <button className="btn btn-sm p-2 rounded-circle"
                          style={{
                            backgroundColor: "var(--white)",
                            color: "var(--primary-blue)"
                          }}
                        >
                          <FontAwesomeIcon icon={faBookmark} />
                        </button>
                      </div>
                    </div>

                    {/* Post Content */}
                    <div className="p-4">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <span className="badge px-3 py-1"
                          style={{
                            backgroundColor: `rgba(var(--${post.imageColor}-rgb), 0.1)`,
                            color: `var(--${post.imageColor})`,
                            fontWeight: "500"
                          }}
                        >
                          {post.category}
                        </span>
                        <small className="text-muted d-flex align-items-center gap-1">
                          <FontAwesomeIcon icon={faClock} style={{ fontSize: "0.7rem" }} />
                          {post.readTime}
                        </small>
                      </div>

                      <h5 className="fw-bold mb-3" style={{ 
                        color: "var(--primary-dark)",
                        minHeight: "60px"
                      }}>
                        {post.title}
                      </h5>

                      <p className="small mb-4" style={{ 
                        color: "var(--text-primary)",
                        lineHeight: 1.6
                      }}>
                        {post.excerpt}
                      </p>

                      {/* Tags */}
                      <div className="d-flex flex-wrap gap-2 mb-4">
                        {post.tags.map((tag, idx) => (
                          <span key={idx} className="badge px-2 py-1"
                            style={{
                              backgroundColor: "rgba(var(--primary-blue-rgb), 0.05)",
                              color: "var(--primary-blue)",
                              fontSize: "0.75rem"
                            }}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>

                      {/* Post Footer */}
                      <div className="d-flex justify-content-between align-items-center pt-3 border-top"
                        style={{ borderColor: "rgba(var(--primary-blue-rgb), 0.1)" }}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <div className="rounded-circle d-flex align-items-center justify-content-center"
                            style={{
                              width: "36px",
                              height: "36px",
                              backgroundColor: `rgba(var(--${post.imageColor}-rgb), 0.1)`,
                              color: `var(--${post.imageColor})`
                            }}
                          >
                            <FontAwesomeIcon icon={faUser} />
                          </div>
                          <div>
                            <div className="small fw-bold" style={{ color: "var(--primary-dark)" }}>
                              {post.author}
                            </div>
                            <div className="small text-muted">
                              {post.date}
                            </div>
                          </div>
                        </div>
                        <motion.button
                          className="btn btn-sm px-3"
                          style={{
                            backgroundColor: `var(--${post.imageColor})`,
                            color: post.imageColor === "gold" ? "var(--primary-dark)" : "var(--white)",
                            fontWeight: "500"
                          }}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleReadMore(post)}
                        >
                          Read More
                          <FontAwesomeIcon icon={faChevronRight} className="ms-2" />
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              ))}
            </motion.div>

           
          </div>

          {/* Sidebar */}
          <div className="col-lg-4">
            {/* About Blog Card */}
            <motion.div
              className="mb-4 rounded-4 p-4"
              style={{
                backgroundColor: "var(--white)",
                border: "1px solid rgba(var(--primary-blue-rgb), 0.1)",
                boxShadow: "var(--shadow-soft)"
              }}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={itemVariants}
            >
              <div className="d-flex align-items-start gap-3 mb-3">
                <div className="p-2 rounded-3 flex-shrink-0"
                  style={{
                    backgroundColor: "rgba(var(--gold-rgb), 0.1)",
                    color: "var(--gold)"
                  }}
                >
                  <FontAwesomeIcon icon={faScaleBalanced} />
                </div>
                <div>
                  <h5 className="fw-bold mb-2" style={{ color: "var(--primary-dark)" }}>
                    About Our Legal Blog
                  </h5>
                  <p className="small mb-0" style={{ color: "var(--text-primary)" }}>
                    Expert legal insights, analysis, and practical guidance from experienced attorneys at RNA Law Chambers.
                  </p>
                </div>
              </div>
              <div className="vstack gap-2 mt-3">
                <div className="d-flex align-items-center justify-content-between small">
                  <span style={{ color: "var(--text-primary)" }}>Articles Published</span>
                  <span className="fw-bold" style={{ color: "var(--gold)" }}>{blogPosts.length}</span>
                </div>
                <div className="d-flex align-items-center justify-content-between small">
                  <span style={{ color: "var(--text-primary)" }}>Expert Authors</span>
                  <span className="fw-bold" style={{ color: "var(--gold)" }}>8</span>
                </div>
                <div className="d-flex align-items-center justify-content-between small">
                  <span style={{ color: "var(--text-primary)" }}>Monthly Readers</span>
                  <span className="fw-bold" style={{ color: "var(--gold)" }}>10K+</span>
                </div>
              </div>
            </motion.div>

            {/* Popular Tags */}
            <motion.div
              className="mb-4 rounded-4 p-4"
              style={{
                backgroundColor: "var(--white)",
                border: "1px solid rgba(var(--primary-blue-rgb), 0.1)",
                boxShadow: "var(--shadow-soft)"
              }}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={itemVariants}
            >
              <h6 className="fw-bold mb-3 d-flex align-items-center gap-2"
                style={{ color: "var(--primary-dark)" }}
              >
                <FontAwesomeIcon icon={faTag} style={{ color: "var(--gold)" }} />
                Popular Topics
              </h6>
              <div className="d-flex flex-wrap gap-2">
                {popularTags.map((tag, index) => (
                  <motion.span
                    key={index}
                    className="badge px-3 py-2"
                    style={{
                      backgroundColor: "rgba(var(--primary-blue-rgb), 0.05)",
                      color: "var(--primary-blue)",
                      fontSize: "0.85rem",
                      fontWeight: "500",
                      borderRadius: "6px",
                      cursor: "pointer",
                      transition: "var(--transition)"
                    }}
                    whileHover={{ 
                      backgroundColor: "var(--primary-blue)",
                      color: "var(--white)",
                      scale: 1.05
                    }}
                  >
                    {tag}
                  </motion.span>
                ))}
              </div>
            </motion.div>

            {/* Recent Comments */}
            <motion.div
              className="mb-4 rounded-4 p-4"
              style={{
                backgroundColor: "var(--white)",
                border: "1px solid rgba(var(--primary-blue-rgb), 0.1)",
                boxShadow: "var(--shadow-soft)"
              }}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={itemVariants}
            >
              <h6 className="fw-bold mb-3 d-flex align-items-center gap-2"
                style={{ color: "var(--primary-dark)" }}
              >
                <FontAwesomeIcon icon={faComment} style={{ color: "var(--gold)" }} />
                Recent Comments
              </h6>
              <div className="vstack gap-3">
                {recentComments.map((comment, index) => (
                  <div key={index} className="border-bottom pb-3"
                    style={{ borderColor: "rgba(var(--primary-blue-rgb), 0.1)" }}
                  >
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div className="fw-bold small" style={{ color: "var(--primary-dark)" }}>
                        {comment.author}
                      </div>
                      <small className="text-muted">{comment.time}</small>
                    </div>
                    <p className="small mb-1" style={{ color: "var(--text-primary)" }}>
                      "{comment.comment}"
                    </p>
                    <div className="small text-muted">
                      On: {comment.post}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            
          </div>
        </div>
      </div>
    </div>
  );
};